import { Centrifuge, Subscription } from 'centrifuge'
import { CENTRIFUGO_WS_URL } from '../../../environment/centrifugo'
import { User } from '../../../model/User'
import { useCallback, useEffect, useRef } from 'react'
import { NotificationEventType } from '../../../model/NotificationSubscriber'
import { useEphemeralNotificationEvent } from './useEphemeralNotificationEvent.hook'
import { useGetTaskCount } from '../../queries/task'
import { EphemeralNotification } from '../../../model/EphemeralNotification'
import { NotificationType, ServerSentNotification } from '../../../model/Notification'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { notificationsKeys } from '../../queries/notifications'
import { PaginatedList } from '../../../model/Pagination'
import { isDefined } from '@follow/cdk'
import { searchLimit } from '../../../constants'
import { StatsAssistantEvent } from '../../../model/StatsAssistant'
import { useStatsAssistantEvents } from '../statsAssistant'

export const useNotificationSubscriber = (loggedUser: User | null) => {
  const { handleEphemeralNotificationEvent } = useEphemeralNotificationEvent()
  const { refetch: refetchUnreadTaskCount } = useGetTaskCount(
    { read: false },
    { infinityStale: true },
  )
  const queryClient = useQueryClient()
  const { handleEvent: handleStatsAssistantEvent, resyncProcessingTurns } =
    useStatsAssistantEvents()

  /* Mise à jour manuelle du cache RQ pour éviter un call API inutile */
  const handleNotificationEvent = useCallback(
    (notification: ServerSentNotification) => {
      const cacheNotificationType =
        notification.type === NotificationType.feature
          ? [NotificationType.feature]
          : [NotificationType.announcement, NotificationType.incident]

      const queryKey = notificationsKeys.unread(cacheNotificationType, true)
      const queryData =
        queryClient.getQueryData<InfiniteData<PaginatedList<ServerSentNotification>>>(queryKey)

      const newdata = structuredClone(queryData)
      const lastPage: PaginatedList<ServerSentNotification> = newdata?.pages.pop() ?? {
        currentPage: 1,
        itemCount: 0,
        limit: searchLimit,
        pageCount: 1,
        items: [],
      }

      lastPage.itemCount += 1
      lastPage.items.push(notification)

      const updatedData: typeof queryData = isDefined(newdata)
        ? {
            ...newdata,
            pages: [...(newdata?.pages ?? []), lastPage],
          }
        : undefined

      queryClient.setQueryData(queryKey, updatedData)
    },
    [queryClient],
  )

  // Handlers lus via ref : les mettre en dépendances recréerait la connexion à chaque rendu.
  const handlersRef = useRef({
    handleEphemeralNotificationEvent,
    handleNotificationEvent,
    handleStatsAssistantEvent,
    refetchUnreadTaskCount,
    resyncProcessingTurns,
  })
  handlersRef.current = {
    handleEphemeralNotificationEvent,
    handleNotificationEvent,
    handleStatsAssistantEvent,
    refetchUnreadTaskCount,
    resyncProcessingTurns,
  }

  const userId = loggedUser?.id
  const subscriberToken = loggedUser?.subscriberToken

  useEffect(() => {
    if (!userId) return
    if (!CENTRIFUGO_WS_URL) {
      return
    }
    if (!subscriberToken) {
      return
    }

    const centrifuge = new Centrifuge(CENTRIFUGO_WS_URL, {
      token: subscriberToken,
    })

    centrifuge.on('error', (ctx) => console.error('[Centrifugo] error', ctx))

    const subs: Subscription[] = []

    const subscribe = (channel: string, type: NotificationEventType) => {
      const sub = centrifuge.newSubscription(channel)
      sub.on('error', (ctx) => console.error('[Centrifugo] subscription error', channel, ctx))
      sub.on('publication', (ctx) => {
        const payload = ctx.data
        const {
          handleEphemeralNotificationEvent,
          handleNotificationEvent,
          handleStatsAssistantEvent,
          refetchUnreadTaskCount,
        } = handlersRef.current

        try {
          switch (type) {
            case NotificationEventType.ephemeralNotification:
              handleEphemeralNotificationEvent(
                typeof payload === 'string'
                  ? JSON.parse(payload)
                  : (payload as EphemeralNotification),
              )
              break
            case NotificationEventType.notification:
              handleNotificationEvent(
                typeof payload === 'string'
                  ? JSON.parse(payload)
                  : (payload as ServerSentNotification),
              )
              break
            case NotificationEventType.task:
              refetchUnreadTaskCount()
              break
            case NotificationEventType.statsAssistant:
              handleStatsAssistantEvent(
                typeof payload === 'string'
                  ? JSON.parse(payload)
                  : (payload as StatsAssistantEvent),
              )
              break
          }
        } catch (e) {
          console.error('[Centrifugo] message parse error', e)
        }
      })
      subs.push(sub)
    }

    subscribe('follow_global_notification', NotificationEventType.notification)
    subscribe('follow_private_notification_' + userId, NotificationEventType.notification)
    subscribe('follow_global_ephemeral_notification', NotificationEventType.ephemeralNotification)
    subscribe(
      'follow_private_ephemeral_notification_' + userId,
      NotificationEventType.ephemeralNotification,
    )
    subscribe('follow_tasks_' + userId, NotificationEventType.task)
    subscribe('stats_assistant_' + userId, NotificationEventType.statsAssistant)

    // Un done publié pendant une coupure est perdu : resynchronisation à chaque (re)connexion.
    centrifuge.on('connected', () => handlersRef.current.resyncProcessingTurns())

    // On appelle subscribe() UNE seule fois.
    // Centrifuge-js gère ensuite automatiquement le resubscribe lors des reconnexions.
    for (const s of subs) s.subscribe()

    centrifuge.connect()

    return () => {
      for (const s of subs) s.unsubscribe()
      centrifuge.disconnect()
    }
  }, [userId, subscriberToken])
}
